package com.appsyde.shared.presentation.screen.component.message.helper

import com.appsyde.shared.modules.ai.domain.model.DetectedFieldV2

 fun String.norm(): String = trim().lowercase()

 fun List<DetectedFieldV2>?.byKey(): Map<String, String> =
    this.orEmpty()
        .mapNotNull { f ->
            val k = f.key?.trim().orEmpty().lowercase()
            val v = f.value?.trim().orEmpty()
            if (k.isBlank() || v.isBlank()) null else k to v
        }
        .toMap()

fun intentLabelFr(intent: String?): String? {
    val i = intent?.norm().orEmpty()
    if (i.isBlank()) return null

    return when (i) {
        // --- Nouveau set (IA)
        "order_tracking" -> "Suivi de commande"
        "billing_question" -> "Facturation"
        "product_question" -> "Question produit"
        "bug_report" -> "Bug"
        "general_question" -> "Question"
        "cancellation" -> "Annulation"

        // --- Legacy ecom
        "refund_request" -> "Remboursement"
        "return_request" -> "Retour"
        "replacement_request" -> "Remplacement"
        "damaged_product", "product_damaged" -> "Produit endommagé"
        "missing_item" -> "Article manquant"
        "wrong_item" -> "Mauvais article"

        // --- Legacy delivery
        "delivery_issue" -> "Problème livraison"
        "tracking_request" -> "Suivi de livraison"

        // --- Legacy billing
        "invoice_question" -> "Facturation"
        "payment_issue" -> "Paiement"
        "subscription_cancel" -> "Résiliation"

        // --- Legacy account
        "account_locked" -> "Compte bloqué"
        "password_reset" -> "Réinit. mot de passe"
        "email_change" -> "Changement d’email"

        else -> null
    }
}

/** Emoji pour la liste tickets (aligné sur intentLabelFr). */
fun intentToIcon(intent: String?): String {
    val i = intent?.norm().orEmpty()
    if (i.isBlank()) return "📋"
    return when (i) {
        "refund_request" -> "💳"
        "return_request", "replacement_request" -> "📦"
        "damaged_product", "product_damaged" -> "🧩"
        "missing_item" -> "📦"
        "wrong_item" -> "📦"
        "delivery_issue", "tracking_request" -> "🚚"
        "invoice_question", "payment_issue", "subscription_cancel" -> "🧾"
        "account_locked", "password_reset", "email_change" -> "🔐"
        else -> "📋"
    }
}

 enum class IntentFamily { ECOM, DELIVERY, BILLING, ACCOUNT, OTHER }

 fun intentFamily(intent: String?): IntentFamily {
    return when (intent?.norm()) {
        "refund_request", "return_request", "replacement_request",
        "damaged_product", "product_damaged", "missing_item", "wrong_item" -> IntentFamily.ECOM

        "delivery_issue", "tracking_request" -> IntentFamily.DELIVERY

        "invoice_question", "payment_issue", "subscription_cancel" -> IntentFamily.BILLING

        "account_locked", "password_reset", "email_change" -> IntentFamily.ACCOUNT

        else -> IntentFamily.OTHER
    }
}

/** Évite le double # si la valeur vient déjà avec un # (ex. dé-anonymisation). */
fun withHash(s: String): String = s.trim().let { if (it.startsWith("#")) it else "#$it" }

fun formatAnchor(family: IntentFamily, m: Map<String, String>): String? {

    fun order() = m["order_number"] ?: m["order_id"]
    fun product() = m["product_name"]
    fun tracking() = m["tracking_number"]
    fun invoice() = m["invoice_number"]
    fun email() = m["customer_email"] ?: m["email"]
    fun accountId() = m["account_id"]
    fun amount() = m["amount"]
    fun dateRef() = m["date_reference"]
    fun city() = m["delivery_city"]
    fun postcode() = m["delivery_postcode"]

    return when (family) {

        IntentFamily.ECOM -> {
            order()?.let { "Commande ${withHash(it)}" }
                ?: product()
        }

        IntentFamily.DELIVERY -> {
            tracking()?.let { "Suivi ${withHash(it)}" }
                ?: order()?.let { "Commande ${withHash(it)}" }
                ?: city()
                ?: postcode()
        }

        IntentFamily.BILLING -> {
            invoice()?.let { "Facture ${withHash(it)}" }
                ?: amount()?.let { "Montant $it" }
                ?: dateRef()?.let { "Date $it" }
        }

        IntentFamily.ACCOUNT -> {
            email()
                ?: accountId()?.let { "Compte ${withHash(it)}" }
        }

        IntentFamily.OTHER -> {
            order()?.let { "Commande ${withHash(it)}" }
                ?: invoice()?.let { "Facture ${withHash(it)}" }
                ?: tracking()?.let { "Suivi ${withHash(it)}" }
                ?: product()
                ?: email()
        }
    }
}
fun computeHeaderTitle(
    intentLabel: String?,
    subject: String?,
    family: IntentFamily,
    m: Map<String, String>
): String {

    val cleanSubject = subject
        ?.replace("(", "")
        ?.replace(")", "")
        ?.trim()
        ?.takeIf { it.isNotBlank() }

    var base = intentLabel
        ?: cleanSubject
        ?: "(Sans objet)"

    // Pour "Article manquant" : intégrer le nom du produit dans le titre (plus lisible que sur la 2e ligne)
    if (intentLabel == "Article manquant") {
        val missingProduct = m["missing_product_name"]?.takeIf { it.isNotBlank() }
            ?: m["product_name"]?.takeIf { it.isNotBlank() }
        if (missingProduct != null) {
            base = "$base · $missingProduct"
        }
    }

    val anchor = formatAnchor(family, m)

    return if (anchor != null && !base.contains(anchor, ignoreCase = true)) {
        "$base · $anchor"
    } else {
        base
    }
}
fun computeHeaderSubtitle(
    m: Map<String, String>,
    missingKeys: List<String>,
    toConfirmKeys: List<String>,
    titleText: String,
    currentIntent: String? = null
): List<String> {

    fun labelForKey(key: String): String? = when (key) {
        "order_number" -> "Numéro de commande"
        "tracking_number" -> "Numéro de suivi"
        "invoice_number" -> "Numéro de facture"
        "refund_preference" -> "Préférence remboursement"
        "photo_proof" -> "Photo"
        "delivery_proof" -> "Preuve livraison"
        "login_identifier" -> "Identifiant"
        else -> null
    }

    // 1️⃣ PRIORITÉ AUX RISQUES
    missingKeys.firstOrNull()?.let { key ->
        labelForKey(key)?.let { return listOf("Info manquante : $it") }
    }

    toConfirmKeys.firstOrNull()?.let { key ->
        labelForKey(key)?.let { return listOf("À confirmer : $it") }
    }

    // 2️⃣ SINON PREUVES FACTUELLES
    // Pour "Article manquant" : afficher l'article manquant (missing_product_name en priorité, sinon product_name)
    val isMissingItem = currentIntent?.trim()?.lowercase() == "missing_item"

    val candidates = buildList {

        if (isMissingItem) {
            (m["missing_product_name"] ?: m["product_name"])?.let { add(it) }
        } else {
            m["product_name"]?.let { add(it) }
        }

        if (m["photo_proof"] == "provided") {
            add("Photo fournie")
        }

        if (m["delivery_proof"] == "provided") {
            add("Preuve livraison")
        }

        m["amount"]?.let { add("Montant $it") }

        m["date_reference"]?.let { add("Date $it") }
    }
        .distinct()
        .filterNot { titleText.contains(it, ignoreCase = true) }

    return candidates.take(2)
}

